import React, { useEffect, useMemo, useState } from "react";
import ButtonsSection from "../../components/ui/buttons/ButtonsSection";
import CausesList from "../trends/components/CausesList";
import { useCause } from "contexts/CauseContext";
import { useTeam } from "contexts/TeamContext";
import useCauseProcessor from "../../hooks/useCauseProcessor";
import { useCauseAnalysis } from "../trends/hooks/useCauseAnalysis";
import { parseMoodTimestamp } from "utils/common/timestampUtils";
import type { Mood } from "../../types/models";
import styles from "./Reports.module.css";
import { useMoodImages } from "../home/hooks/useMoodImages";
import Feeling from "./Feeling";
import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import type { TeamMember } from "../teams/types";
import { TeamSelect } from "../teams/components/TeamSelect";
import { PeriodSelect } from "./components/PeriodSelect";
import { calculateState } from "./calculateState"
const startOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfWeek = (date: Date) => {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

function lastFullISOWeekRange(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isoDay = ((today.getDay() + 6) % 7) + 1;

  const currentMonday = new Date(today);
  currentMonday.setDate(today.getDate() - (isoDay - 1));

  const prevMonday = new Date(currentMonday);
  prevMonday.setDate(currentMonday.getDate() - 7);

  const prevSunday = new Date(prevMonday);
  prevSunday.setDate(prevMonday.getDate() + 6);

  return { start: prevMonday, end: prevSunday };
}

export const Reports = () => {
  const { teams, teamCauses, teamMoods, teamMembers } = useTeam();

  const [internalDate] = useState(lastFullISOWeekRange().end);
  const { causeTypesTranslations } = useCause();
  const { refreshData, themeId } = useAppState();
  const { userId } = useAuth();
  const { allCauseTypes } = useCauseProcessor();

  const [selectedDate, setSelectedDate] = useState(internalDate);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [isNewTeam, setIsNewTeam] = useState<boolean>(false);
  const selectedWeekStart = useMemo(
    () => startOfWeek(selectedDate),
    [selectedDate]
  );
  const selectedWeekEnd = useMemo(
    () => endOfWeek(selectedDate),
    [selectedDate]
  );



  useEffect(() => {
    if (selectedTeam || teams.length === 0) return;
    setSelectedTeam(teams[0].id);
  }, [teams, selectedTeam]);

  function isSameWeek(dateA: Date, dateB: Date) {
    const startOfWeek = (date: Date) => {
      const result = new Date(date);
      result.setHours(0, 0, 0, 0);

      const day = result.getDay() || 7;
      result.setDate(result.getDate() - day);

      console.log(result);

      return result;
    };

    return startOfWeek(dateA).getTime() === startOfWeek(dateB).getTime();
  }


  useEffect(() => {

    if (!teams) return;
    console.log(
      "debug::: " +
        teams.find((team: any) => {
          return team.id === selectedTeam;
        })?.createdAt
    );
    setIsNewTeam(
      isSameWeek(
        new Date(
          teams.find((team: any) => {
            return team.id === selectedTeam;
          })?.createdAt
        ),
        new Date()
      )
    );
  }, [selectedTeam, teams])

  const selectedTeamMemberIds = useMemo(() => {
    const ids = new Set<string>();
    (teamMembers as TeamMember[])
      .filter(
        (member) => member.teamId === selectedTeam && member.status === "active"
      )
      .forEach((member) => {
        if (member.uid) ids.add(member.uid);
      });
    return ids;
  }, [teamMembers, selectedTeam]);

  const periodFilteredMoods = useMemo(() => {
    const safeMoods = Array.isArray(teamMoods) ? (teamMoods as Mood[]) : [];
    return safeMoods.filter((mood) => {
      if (!selectedTeamMemberIds.has(mood.uid)) return false;
      const parsed = parseMoodTimestamp(mood);
      console.log("parsed " + parsed)
      if (!parsed) return false;
      return parsed >= selectedWeekStart && parsed <= selectedWeekEnd;
    });
  }, [teamMoods, selectedTeamMemberIds, selectedWeekStart, selectedWeekEnd]);

  const { filteredCauses, teamCausesYes } = useCauseAnalysis(
    periodFilteredMoods,
    teamCauses || [],
    allCauseTypes,
    causeTypesTranslations
  );

  const { moodImages, feelingNames, imagesReady } = useMoodImages(
    themeId,
    undefined,
    selectedDate
  );

  const relevantMoods = useMemo(() => {
    const map: Record<number, number> = {};
    periodFilteredMoods.forEach((mood) => {
      const idx = feelingNames.findIndex(
        (label) => label.toLowerCase() === mood.mood.toLowerCase()
      );
      if (idx === -1) return;
      const duration = parseInt(String(mood.duration)) || 1;
      map[idx] = (map[idx] || 0) + duration;
    });
    return map;
  }, [periodFilteredMoods, feelingNames]);

  const checkedInCount = useMemo(() => {
    const checkedInUids = new Set<string>();
    periodFilteredMoods.forEach((mood) => {
      if (mood.uid) checkedInUids.add(mood.uid);
    });
    return checkedInUids.size;
  }, [periodFilteredMoods]);


  const totalTeamMembers = selectedTeamMemberIds.size;

  useEffect(() => {
    if (!userId) return;
    refreshData(userId, "secondary");
  }, [userId, refreshData, selectedTeam]);

  return (
    <div className="root">
      <div className={styles["header-container"]}>
        <span className="page-title">Our week</span>
      </div>

      {teams.length === 0 && (
        <>
          <p>
            No teams. You can add team
            <a href={"http://localhost:3000/app/settings/teams"}> here</a>
          </p>
        </>
      )}
      {teams.length > 0 && (
        <>
          <span className="secondary-title">Input</span>

          <div
            className="select-component__duration-section"
            style={{ overflow: "visible" }}
          >
            <TeamSelect
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              teams={teams}
            />

            {!isNewTeam &&
              <PeriodSelect name={"Period"} onDateChange={setSelectedDate} />
            }
          </div>
        </>
      )}


      {isNewTeam && (
        <>
          See you soon
        </>
      )}



      {teams.length > 0 && !isNewTeam && (
        <div>
          <Feeling
            moodImages={moodImages}
            relevantMoods={relevantMoods}
            imagesReady={imagesReady}
          />

          <div style={{ display: "flex", justifyContent: "center" }}>
            <h1 className="grid-title">{calculateState()}</h1>
          </div>

          <span className="secondary-title">Causes</span>

          <CausesList filteredCauses={teamCausesYes} />

          <p>
            {checkedInCount} of {totalTeamMembers} team members have checked in
          </p>
        </div>
      )}

      <div className="buttons-wrapper">
        <ButtonsSection />
      </div>
    </div>
  );
};
